import { DragDropModule } from '@angular/cdk/drag-drop';
import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ErrorHandler, Inject, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule, MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import { SocketIoConfig, SocketIoModule } from 'ngx-socket-io';

import { LeafletCompatModule } from './compat/leaflet/leaflet.module';
import { NgImageSliderCompatModule } from './compat/ng-image-slider/ng-image-slider.module';
import { NgSelectCompatModule } from './compat/ng-select/ng-select.module';
import { Ng2ChartsCompatModule } from './compat/ng2-charts/ng2-charts.module';

import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './components/header/header.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { LoadingScreenCompatComponent } from './components/loading-screen/loading-screen-compat.component';
import { DirectivesModule } from './directives.module';
import { PhoneNumberPipe } from './pipes/number-phone';
import { AuthService } from './providers/auth/auth.service';
import { CarService } from './providers/car/car.service';
import { CompanyService } from './providers/company/company.service';
import { DispatchService } from './providers/dispatch/dispatch.service';
import { ErrorService } from './providers/error/error.service';
import { FileService } from './providers/file/file.service';
import { GeneralService } from './providers/general/general.service';
import { HereService } from './providers/here/here.service';
import { InterceptorService } from './providers/interceptor/interceptor.service';
import { JoinRequestsService } from './providers/joinRequests/joinRequests.service';
import { LoadingService } from './providers/loading/loading.service';
import { AppErrorhandler } from './providers/myErrorHandler/myErrorHandler.service';
import { NotificationService } from './providers/notification/notification.service';
import { OrderService } from './providers/order/order.service';
import { PolicyService } from './providers/policy/policy.service';
import { RegisterService } from './providers/register/register.service';
import { ReportsService } from './providers/reports/reports.service';
import { UserService } from './providers/user/user.service';
import { LayoutModule } from './layout/layout.module';
import { DriverDetailsRecordModule } from './components/driver-details-record/driver-details-record.module';
import { CoreModule } from './core/core.module';
import { CustomIconsModule } from './core/modules/custom-icons/custom-icons.module';
import { RegisterModule } from './components/register/register.module';
import { TripPlannerModule } from './components/trip-planner/trip-planner.module';
import { CustomControlsModule } from './core/modules/custom-controls/custom-controls.module';
import { CustomViewsModule } from './core/modules/custom-views/custom-views.module';
import { NotificationUiService } from './providers/notification/notification.ui.service';
import { CustomNavigationModule } from './core/modules/custom-navigation/custom-naigation.module';
import { TelemetryService } from './providers/telemetry/telemetry.service';

export const httpInterceptorProviders = [
  { provide: HTTP_INTERCEPTORS, useClass: InterceptorService, multi: true },
];
const config: SocketIoConfig = {
  url: environment.socket_api, options: {
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: Infinity,
    autoConnect: false,
    forceNew: false,
    reconnection: true,
    transports: ['polling'],
    upgrade: false,
  },
};

@NgModule({
  declarations: [
    AppComponent,
    PhoneNumberPipe,
  ],
  imports: [
    LoadingScreenCompatComponent,
    HeaderComponent,
    SidebarComponent,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    DirectivesModule,
    FormsModule,
    HttpClientModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatSidenavModule,
    MatSnackBarModule,
    MatTabsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatMenuModule,
    NgSelectCompatModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatToolbarModule,
    MatTooltipModule,
    MatSlideToggleModule,
    DragDropModule,
    LeafletCompatModule,
    RecaptchaV3Module,
    SocketIoModule.forRoot(config),
    NgImageSliderCompatModule,
    LayoutModule,
    RegisterModule,
    TripPlannerModule,
    Ng2ChartsCompatModule,
    DriverDetailsRecordModule,
    CoreModule,
    CustomIconsModule,
    CustomControlsModule,
    CustomViewsModule,
    CustomNavigationModule.forRoot(),
  ],
  providers: [
    RegisterService,
    AuthService,
    UserService,
    LoadingService,
    NotificationService,
    NotificationUiService,
    ErrorService,
    FileService,
    CompanyService,
    OrderService,
    HereService,
    PolicyService,
    CarService,
    GeneralService,
    DispatchService,
    ReportsService,
    JoinRequestsService,
    DatePipe,
    HttpClientModule,
    PhoneNumberPipe,
    httpInterceptorProviders,
    {
      provide: ErrorStateMatcher,
      useClass: ShowOnDirtyErrorStateMatcher,
    },
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 2000,
        verticalPosition: 'bottom',
        horizontalPosition: 'left',
      },
    },
    { provide: ErrorHandler, useClass: AppErrorhandler },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recatchaSiteKey },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(@Inject(TelemetryService) telemetryService: TelemetryService) {
    void telemetryService.initialize();
  }
}
